import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogPost, getRelatedPosts, BlogPost as BlogPostType } from '../data/blog';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);

  useEffect(() => {
    if (slug) {
      const blogPost = getBlogPost(slug);
      if (blogPost) {
        setPost(blogPost);
        setRelatedPosts(getRelatedPosts(slug, blogPost.category, 3));
        // Scroll to top when post loads
        window.scrollTo(0, 0);
      } else {
        navigate('/blog');
      }
    }
  }, [slug, navigate]);

  if (!post) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      {/* Blog Post Hero */}
      <section className="blog-post-hero position-relative">
        <div 
          className="blog-hero-bg"
          style={{
            backgroundImage: `url(${post.featuredImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            filter: 'brightness(0.4)'
          }}
        ></div>
        <div className="container position-relative py-5" style={{ zIndex: 2 }}>
          <div className="row justify-content-center py-5">
            <div className="col-lg-10">
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-4">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-white text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/blog" className="text-white text-decoration-none">Blog</Link>
                  </li>
                  <li className="breadcrumb-item active text-white" aria-current="page">
                    {post.category}
                  </li>
                </ol>
              </nav>

              {/* Category Badge */}
              <div className="mb-3">
                <span className="badge bg-primary fs-6 px-3 py-2">{post.category}</span>
              </div>

              {/* Title */}
              <h1 className="display-4 fw-bold text-white mb-4">
                {post.title}
              </h1>

              {/* Meta Information */}
              <div className="d-flex flex-wrap align-items-center text-white mb-4">
                <div className="d-flex align-items-center me-4 mb-2">
                  <i className="fas fa-user-circle fa-2x me-2"></i>
                  <div>
                    <div className="fw-bold">{post.author.name}</div>
                    <div className="small">{post.author.role}</div>
                  </div>
                </div>
                <div className="me-4 mb-2">
                  <i className="far fa-calendar-alt me-2"></i>
                  {formatDate(post.publishedDate)}
                </div>
                <div className="me-4 mb-2">
                  <i className="far fa-clock me-2"></i>
                  {post.readTime}
                </div>
                {post.views && (
                  <div className="mb-2">
                    <i className="far fa-eye me-2"></i>
                    {post.views} views
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="blog-post-tags">
                {post.tags.map((tag: string, idx: number) => (
                  <span key={idx} className="badge bg-light text-dark me-2 mb-2">
                    <i className="fas fa-tag me-1"></i>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Post Content */}
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row">
              {/* Main Content */}
              <div className="col-lg-8">
                <article className="blog-post-content card border-0 shadow-sm p-4 mb-5">
                  <div 
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Share Buttons */}
                  <div className="blog-share mt-5 pt-4 border-top">
                    <h5 className="mb-3">Share this article</h5>
                    <div className="d-flex gap-2">
                      <button className="btn btn-outline-primary">
                        <i className="fab fa-facebook-f me-2"></i>
                        Facebook
                      </button>
                      <button className="btn btn-outline-info">
                        <i className="fab fa-twitter me-2"></i>
                        Twitter
                      </button>
                      <button className="btn btn-outline-success">
                        <i className="fab fa-whatsapp me-2"></i>
                        WhatsApp
                      </button>
                      <button className="btn btn-outline-primary">
                        <i className="fab fa-linkedin-in me-2"></i>
                        LinkedIn
                      </button>
                    </div>
                  </div>

                  {/* Author Bio */}
                  <div className="author-bio mt-5 p-4 bg-light rounded">
                    <div className="d-flex align-items-start">
                      <div className="me-3">
                        <i className="fas fa-user-circle fa-4x text-primary"></i>
                      </div>
                      <div>
                        <h5 className="mb-1">About {post.author.name}</h5>
                        <p className="text-muted mb-2">{post.author.role}</p>
                        <p className="mb-0">
                          An experienced educator passionate about helping students achieve their goals
                          in competitive examinations.
                        </p>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Navigation */}
                <div className="blog-navigation d-flex justify-content-between mb-5">
                  <Link to="/blog" className="btn btn-outline-primary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Blog
                  </Link>
                </div>
              </div>

              {/* Sidebar */}
              <div className="col-lg-4">
                {/* CTA Card */}
                <div className="sidebar-cta card border-0 shadow-sm mb-4 overflow-hidden">
                  <div 
                    className="card-body p-4 text-white text-center"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                  >
                    <i className="fas fa-graduation-cap fa-3x mb-3"></i>
                    <h5 className="mb-3">Start Learning Today</h5>
                    <p className="mb-3">
                      Join WiZiAi and get personalized learning experience
                    </p>
                    <Link to="/signup" className="btn wiziai-btn-primary">
                      Get Started
                      <i className="fas fa-arrow-right ms-2"></i>
                    </Link>
                  </div>
                </div>

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <div className="related-posts card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h5 className="mb-4">
                        <i className="fas fa-bookmark me-2 text-primary"></i>
                        Related Articles
                      </h5>
                      <div className="d-flex flex-column gap-3">
                        {relatedPosts.map((relatedPost) => (
                          <Link
                            key={relatedPost.id}
                            to={`/blog/${relatedPost.slug}`}
                            className="text-decoration-none"
                          >
                            <div className="related-post-item p-3 border rounded hover-shadow">
                              <h6 className="mb-2 text-dark">{relatedPost.title}</h6>
                              <div className="small text-muted">
                                <i className="far fa-clock me-1"></i>
                                {relatedPost.readTime}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* More Articles CTA */}
      <div className="container-fluid bg-light py-5">
        <div className="container text-center">
          <h3 className="mb-4">Want to Read More?</h3>
          <p className="text-muted mb-4">
            Explore our comprehensive collection of study guides and exam preparation tips
          </p>
          <Link to="/blog" className="btn btn-primary btn-lg">
            <i className="fas fa-book-open me-2"></i>
            View All Articles
          </Link>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
